import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, Plus, Briefcase } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { PnlChart } from './PnlChart'
import { PositionsList } from './PositionsList'
import { useActivePositions, usePositionSnapshots } from '@/hooks'
import { getMultipleQuotes } from '@/lib/services/stockPrice'
import type { StockQuote } from '@/lib/services/stockPrice'

interface ActivePositionsSectionProps {
  isAdmin: boolean
  onAddPosition: () => void
  onEditPosition: (id: string) => void
}

const POSITION_QUOTES_KEY = (symbols: string[]) => [
  'position-quotes',
  symbols.sort().join(','),
]

export function ActivePositionsSection({
  isAdmin,
  onAddPosition,
  onEditPosition,
}: ActivePositionsSectionProps) {
  const { t } = useTranslation()
  const { data: positions = [], isLoading: positionsLoading } =
    useActivePositions()
  const { data: snapshots = [] } = usePositionSnapshots(30)

  const tickers = useMemo(
    () => positions.map((p) => p.ticker).filter(Boolean),
    [positions]
  )

  const { data: quotes = new Map<string, StockQuote>(), isLoading: quotesLoading } =
    useQuery<Map<string, StockQuote>>({
      queryKey: POSITION_QUOTES_KEY(tickers),
      queryFn: () => getMultipleQuotes(tickers),
      enabled: tickers.length > 0,
      staleTime: 5 * 60_000,
      refetchInterval: 5 * 60_000,
    })

  if (positionsLoading) {
    return (
      <Card className="bg-neutral-950/60 border-white/[0.06]">
        <CardContent className="py-8">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-neutral-800/50 animate-pulse" />
            <div className="w-32 h-4 bg-neutral-800/50 rounded animate-pulse" />
          </div>
        </CardContent>
      </Card>
    )
  }

  const hasPositions = positions.length > 0

  return (
    <Card className="bg-neutral-950/60 border-white/[0.06]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/20 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            </div>
            <h2 className="text-sm font-semibold text-white">
              {t('activePositions')}
            </h2>
          </div>
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2.5 text-neutral-400 hover:text-white"
              onClick={onAddPosition}
            >
              <Plus className="w-4 h-4 mr-1" />
              <span className="text-xs">{t('addPosition')}</span>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {hasPositions ? (
          <div className="space-y-4">
            <PnlChart snapshots={snapshots} />
            <PositionsList
              positions={positions}
              quotes={quotes}
              quotesLoading={quotesLoading}
              isAdmin={isAdmin}
              onEditPosition={onEditPosition}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-2xl bg-neutral-900/50 border border-white/[0.06] flex items-center justify-center mb-3">
              <Briefcase className="w-6 h-6 text-neutral-600" />
            </div>
            <p className="text-neutral-400 text-sm">
              {t('noActivePositions')}
            </p>
            <p className="text-neutral-600 text-xs mt-1">
              {t('positionsWillAppear')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
